import type { SupportedSignalName } from 'features/smartSignals'
import { AppProduct } from 'models'

/** Human-readable display names for AppProduct values */
export const APP_PRODUCT_DISPLAY_NAMES: Partial<Record<AppProduct, string>> = {
  [AppProduct.Botd]: 'Bot Detection',
  [AppProduct.Incognito]: 'Incognito Detection',
  [AppProduct.Tampering]: 'Tampering Detection',
  [AppProduct.Vpn]: 'VPN Detection',
  [AppProduct.Tor]: 'Tor Detection',
  [AppProduct.IpBlacklist]: 'IP Blocklist',
  [AppProduct.Proxy]: 'Residential Proxy',
  [AppProduct.PrivacySettings]: 'Privacy Settings',
  [AppProduct.VirtualMachine]: 'Virtual Machine Detection',
  [AppProduct.HighActivity]: 'High-Activity Device',
  [AppProduct.DeveloperTools]: 'Developer Tools',
  [AppProduct.MobileRootApps]: 'Rooted Device Detection',
  [AppProduct.MobileEmulator]: 'Android Emulator',
  [AppProduct.MobileSimulator]: 'iOS Simulator',
  [AppProduct.MobileAppCloners]: 'Cloned App Detection',
  [AppProduct.MobileJailbreak]: 'Jailbreak Detection',
  [AppProduct.MobileFrida]: 'Frida Detection',
  [AppProduct.MobileGeolocationSpoofing]: 'Geolocation Spoofing',
  [AppProduct.MitmAttack]: 'MitM Attack Detection',
  [AppProduct.ActiveCall]: 'Active Call Detection',
  [AppProduct.LocationSpoofing]: 'Location Spoofing',
  [AppProduct.MobileRecentFactoryReset]: 'Factory Reset Detection',
  [AppProduct.SuspectScore]: 'Suspect Score',
  [AppProduct.DeviceRarity]: 'Rare device detection',
}

/** Map SmartSignals page signal names to AppProduct */
export const SIGNAL_NAME_TO_APP_PRODUCT: Record<SupportedSignalName, AppProduct> = {
  totalGoodBots: AppProduct.Botd,
  totalBadBots: AppProduct.Botd,
  totalIncognito: AppProduct.Incognito,
  totalTampering: AppProduct.Tampering,
  totalVpn: AppProduct.Vpn,
  totalProxy: AppProduct.Proxy,
  totalIpBlocklist: AppProduct.IpBlacklist,
  totalVirtualMachines: AppProduct.VirtualMachine,
  totalPrivacySettings: AppProduct.PrivacySettings,
  totalHighActivity: AppProduct.HighActivity,
  totalEmulator: AppProduct.MobileEmulator,
  totalSimulator: AppProduct.MobileSimulator,
  totalAppCloners: AppProduct.MobileAppCloners,
  totalFrida: AppProduct.MobileFrida,
  totalJailbreak: AppProduct.MobileJailbreak,
  totalRootApps: AppProduct.MobileRootApps,
  totalTor: AppProduct.Tor,
  totalMitmAttack: AppProduct.MitmAttack,
  totalGeolocationSpoofing: AppProduct.MobileGeolocationSpoofing,
  totalDeveloperTools: AppProduct.DeveloperTools,
  totalDeviceRarity: AppProduct.DeviceRarity,
  totalActiveCall: AppProduct.ActiveCall,
}

/** Map event detail signal definition keys to AppProduct */
export const SIGNAL_DEFINITION_KEY_TO_APP_PRODUCT: Record<string, AppProduct> = {
  'bot-detection': AppProduct.Botd,
  vpn: AppProduct.Vpn,
  'ip-blocklist': AppProduct.IpBlacklist,
  proxy: AppProduct.Proxy,
  tor: AppProduct.Tor,
  'high-activity': AppProduct.HighActivity,
  tampering: AppProduct.Tampering,
  incognito: AppProduct.Incognito,
  'virtual-machine': AppProduct.VirtualMachine,
  'privacy-settings': AppProduct.PrivacySettings,
  'developer-tools': AppProduct.DeveloperTools,
  emulator: AppProduct.MobileEmulator,
  simulator: AppProduct.MobileSimulator,
  'cloned-app': AppProduct.MobileAppCloners,
  'factory-reset': AppProduct.MobileRecentFactoryReset,
  frida: AppProduct.MobileFrida,
  'location-spoofing': AppProduct.MobileGeolocationSpoofing,
  jailbreak: AppProduct.MobileJailbreak,
  'rooted-app': AppProduct.MobileRootApps,
  'mitm-attack': AppProduct.MitmAttack,
  'device-rarity': AppProduct.DeviceRarity,
  'active-call': AppProduct.ActiveCall,
}

/** Map identification table column IDs to AppProduct */
export const COLUMN_ID_TO_APP_PRODUCT: Record<string, AppProduct> = {
  bot: AppProduct.Botd,
  incognito: AppProduct.Incognito,
  vpn: AppProduct.Vpn,
  browserTampering: AppProduct.Tampering,
  virtualMachine: AppProduct.VirtualMachine,
  privacySettings: AppProduct.PrivacySettings,
  developerTools: AppProduct.DeveloperTools,
  ipBlocklist: AppProduct.IpBlacklist,
  residentialProxy: AppProduct.Proxy,
  torNode: AppProduct.Tor,
  suspectScore: AppProduct.SuspectScore,
  mitmAttack: AppProduct.MitmAttack,
  jailbroken: AppProduct.MobileJailbreak,
  emulator: AppProduct.MobileEmulator,
  simulator: AppProduct.MobileSimulator,
  rootedDevice: AppProduct.MobileRootApps,
  clonedApp: AppProduct.MobileAppCloners,
  frida: AppProduct.MobileFrida,
  locationSpoofing: AppProduct.MobileGeolocationSpoofing,
  deviceRarity: AppProduct.DeviceRarity,
  activeCall: AppProduct.ActiveCall,
}

/** Map identification filter keys to AppProduct */
export const FILTER_KEY_TO_APP_PRODUCT: Record<string, AppProduct> = {
  bot: AppProduct.Botd,
  botName: AppProduct.Botd,
  category: AppProduct.Botd,
  provider: AppProduct.Botd,
  identity: AppProduct.Botd,
  confidence: AppProduct.Botd,
  incognito: AppProduct.Incognito,
  vpn: AppProduct.Vpn,
  tampering: AppProduct.Tampering,
  virtualMachine: AppProduct.VirtualMachine,
  privacySettings: AppProduct.PrivacySettings,
  developerTools: AppProduct.DeveloperTools,
  ipBlocklist: AppProduct.IpBlacklist,
  proxy: AppProduct.Proxy,
  torNode: AppProduct.Tor,
  minSuspectScore: AppProduct.SuspectScore,
  mitmAttack: AppProduct.MitmAttack,
  jailbroken: AppProduct.MobileJailbreak,
  emulator: AppProduct.MobileEmulator,
  simulator: AppProduct.MobileSimulator,
  rootApps: AppProduct.MobileRootApps,
  clonedApp: AppProduct.MobileAppCloners,
  frida: AppProduct.MobileFrida,
  locationSpoofing: AppProduct.MobileGeolocationSpoofing,
  deviceRarity: AppProduct.DeviceRarity,
  activeCall: AppProduct.ActiveCall,
}

/** Get display names for disabled products (products not in the enabled set) */
export function getDisabledProductNames(
  enabledProducts: AppProduct[],
  allProducts: AppProduct[] = Object.keys(APP_PRODUCT_DISPLAY_NAMES) as AppProduct[]
): string[] {
  const enabledSet = new Set(enabledProducts)
  return allProducts
    .filter((product) => !enabledSet.has(product))
    .map((product) => APP_PRODUCT_DISPLAY_NAMES[product])
    .filter((name): name is string => name != null)
}
